import React from 'react';
import { Helmet } from 'react-helmet-async';
import { sanitizeForMeta, sanitizeStructuredData, sanitizeUrl } from '../utils/security';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  structuredData?: object;
}

export default function SEO({
  title = 'Local Artist Fairs - Find Fresh Produce & Farm Markets Near You',
  description = 'Discover local artist fairs, farmers markets, and fresh produce near you. Connect directly with local farmers for organic vegetables, fruits, eggs, honey, and seasonal products.',
  keywords = 'artist fairs, local artist fairs, farmers market, fresh produce, organic vegetables',
  image = 'https://localartistfairs.com/og-image.jpg',
  url = 'https://localartistfairs.com',
  type = 'website',
  structuredData
}: SEOProps) {
  // Sanitize all inputs
  const safeTitle = sanitizeForMeta(title);
  const safeDescription = sanitizeForMeta(description);
  const safeKeywords = sanitizeForMeta(keywords);
  const safeUrl = sanitizeUrl(url);
  const safeImage = sanitizeUrl(image);

  const siteTitle = safeTitle.includes('Local Artist Fairs')
    ? safeTitle
    : `${safeTitle} | Local Artist Fairs`;

  // Default structured data for organization
  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Local Artist Fairs",
    "url": "https://localartistfairs.com",
    "logo": "https://localartistfairs.com/logo.png",
    "description": "Directory of local artist fairs and farmers markets",
    "sameAs": [
      "https://facebook.com/localartistfairs",
      "https://instagram.com/localartistfairs"
    ]
  };

  // Sanitize structured data
  const safeStructuredData = structuredData
    ? sanitizeStructuredData(structuredData)
    : defaultStructuredData;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{siteTitle}</title>
      <meta name="description" content={safeDescription} />
      <meta name="keywords" content={safeKeywords} />
      <link rel="canonical" href={safeUrl} />

      {/* Open Graph Tags */}
      <meta property="og:title" content={siteTitle} />
      <meta property="og:description" content={safeDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={safeUrl} />
      <meta property="og:image" content={safeImage} />
      <meta property="og:site_name" content="Local Artist Fairs" />
      <meta property="og:locale" content="en_US" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(safeStructuredData)}
      </script>
    </Helmet>
  );
}